/* eslint-disable react/no-unknown-property */
import React, { useState, useEffect, useContext } from 'react';
import proptypes from 'prop-types';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { Carousel } from '@thd-olt-component-react/carousel';
import { useHelmet, LinkTag } from '@thd-nucleus/thd-helmet';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import {
  params,
  customType,
  fragment,
  shape as shapeType,
  string as stringType,
  bool as boolType,
  useDataModel
} from '@thd-nucleus/data-sources';
import classNames from 'classnames/bind';
import {
  initImpressionWatchers
} from '../../utils/initImpressionWatchers';
import {
  getSchema,
  getDefaultBanner,
  shouldSkipDataModel,
  shouldSuppressBanner
} from '../../utils/helpers';
import {
  triggerNewRelic,
  triggerNewRelicTimeLog
} from '../../utils/newRelicHelpers';
import {
  buildRevJetBanner
} from '../../utils/revjet';

import styles from './SponsoredBanner.module.scss';
import { newRelicConstants } from '../../constants';
import useFetch from '../../hooks/useFetch';
import {
  bannerTypes,
  SUPPRESSED_NVALUES
} from '../../constants/constants';

const { string, bool, shape, object } = proptypes;

let cx = classNames.bind(styles);
const hoistedImgFunction = ({ data }) => {
  const {
    isBrowse, isSearch, avoidPreload, defaultBannerInfo, isBannerSuppressed
  } = data;
  if ((isBrowse || isSearch) && !avoidPreload && !isBannerSuppressed) {
    return [
      new LinkTag({
        rel: 'preload',
        as: 'image',
        href: defaultBannerInfo.imgSrc,
        id: 'spBannerPreloadLink',
        fetchpriority: 'high'
      }),
    ];
  }
  return null;
};

const SponsoredBanner = (props) => {
  let {
    avoidPreload,
    pageContext,
    schema,
    slugId,
    utilizeRvData,
    contentfulDefaultBanner,
  } = props;

  let { mcvisID, guestUserID } = useThdCustomer();
  const [hasData, setHasData] = useState(false);
  const experienceContext = useContext(ExperienceContext);

  // Ad Server Testing
  const suppressedNValues = useConfigService(SUPPRESSED_NVALUES);

  const skip = shouldSkipDataModel(schema);
  const resp = useDataModel('container', {
    skip,
    variables: {
      context: {
        id: '4OH5iatZ0vFnTL58Qg8eJv'
      }
    }
  });
  const { data: defaultBanner } = resp;
  const validatedDefaultBanner = defaultBanner?.container || contentfulDefaultBanner;

  const validBrowserId = mcvisID;

  const defaultBannerInfo = getDefaultBanner(experienceContext, validatedDefaultBanner);
  const [isBannerSuppressed, _] = useState(shouldSuppressBanner({ experienceContext, pageContext, suppressedNValues }));
  schema = getSchema({ schema, pageContext });
  const schemaId = schema + '__banner';

  const data = {
    avoidPreload,
    channel: experienceContext.channel,
    isBrowse: pageContext?.isBrowse,
    isSearch: pageContext?.isSearch,
    defaultBannerInfo,
    isBannerSuppressed
  };

  useHelmet('preloadBanner', { data }, hoistedImgFunction);

  const requestConfig = {
    mcvisID,
    guestUserID,
    browserId: validBrowserId,
    schema,
    schemaId,
    experienceContext,
    pageContext,
    setHasData,
    bannerType: bannerTypes.STANDARD,
    slugId,
    utilizeRvData,
    isBannerSuppressed,
  };

  const {
    data: bannerData,
    status,
    apiRequestTimestamp,
    apiResponseTimestamp,
    adServer: bannerApiAdServer
  } = useFetch(requestConfig);

  useEffect(() => {
    const adType = newRelicConstants.STANDARD_BANNER;
    if (status === 200) {
      requestConfig.startTime = apiRequestTimestamp;
      setHasData(true);
      buildRevJetBanner({ data: bannerData, requestConfig, adType });
    }
    if (status) {
      triggerNewRelic(adType, `BANNER-API-${status}`);
      // SEND TIMESTAMPS TO CALCULATE API RESPONSE TIME
      triggerNewRelicTimeLog(newRelicConstants.STANDARD_BANNER_API_RESPONSE, apiRequestTimestamp, apiResponseTimestamp, bannerApiAdServer);
      // SEND TIME FROM BANNER INITIATED TO BANNER WINNER RECEIVED
      triggerNewRelicTimeLog(newRelicConstants.STANDARD_BANNER_AD_INIT_TO_WINNER, apiRequestTimestamp, apiResponseTimestamp, bannerApiAdServer);
    }
  }, [status]);

  useEffect(() => {
    initImpressionWatchers(['NUCLEUS_BANNER']);
    const slideEl = document.querySelector('.carousel .slide');
    if (slideEl) {
      slideEl.style.background = '#f5f5f5';
    }
  }, [hasData]);

  if (isBannerSuppressed) {
    return null;
  }

  return (
    <div
      className={cx('sponsored_banner_container')}
      id="sponsored-standard-banner-nucleus"
      data-component="SponsoredBanner"
    >

      {!hasData && (
        <div
          id="default_banner_carousel"
          style={defaultBannerInfo.spacingCss}
          data-testid="default-standard-banner"
        >
          <a href={defaultBannerInfo.href}>
            {/* eslint-disable-next-line */}
            <img
              className={cx('sponsored_banner_image')}
              alt="sponsored banner"
              src={defaultBannerInfo.imgSrc}
              fetchpriority="high"
              loading="eager"
            />
          </a>
        </div>
      )}

      {hasData && (

        <Carousel multiItem={false} totalItems={2} transparent>
          <div
            id="default_banner_carousel"
            style={defaultBannerInfo.spacingCss}
            data-testid="default-standard-banner"
          >
            <a href={defaultBannerInfo.href}>
              {/* eslint-disable-next-line */}
              <img
                className={cx('sponsored_banner_image')}
                alt="sponsored banner"
                src={defaultBannerInfo.imgSrc}
                fetchpriority="high"
                loading="eager"
              />
            </a>
          </div>
          <div
            id={schemaId}
            className="nucleus-banner"
            data-usenewimpressionlogic="true"
            data-testid="sponsored-standard-banner"
          />

        </Carousel>
      )}
    </div>
  );
};

SponsoredBanner.dataModel = {
  container: params({
    context: customType('QueryContext')
      .shape({
        id: stringType()
      })
  })
    .shape({
      SponsoredTopBanner: fragment()
        .shape({
          imgDesktop: stringType(),
          imgMobile: stringType(),
          clickthruUrl: stringType(),
          specialDefaultBanner: shapeType({
            imgDesktop: stringType(),
            imgMobile: stringType(),
            clickthruUrl: stringType(),
            useSpecialBanner: boolType()
          })
        }),
    }),
};

SponsoredBanner.propTypes = {
  schema: string,
  browserId: string,
  slugId: string,
  pageContext: shape({
    label: string,
    data: shape(),
    keyword: string,
    isSearch: bool,
    isCategory: bool,
    isBrowse: bool,
  }),
  avoidPreload: bool,
  utilizeRvData: bool,
  showSponsoredBanner: bool,
  contentfulDefaultBanner: object,
  renderHarmony: bool,
};

SponsoredBanner.defaultProps = {
  avoidPreload: true,
  browserId: '',
  pageContext: {},
  schema: '',
  slugId: '',
  utilizeRvData: false,
  showSponsoredBanner: true,
  contentfulDefaultBanner: {},
  renderHarmony: false,
};

export { SponsoredBanner };
